import React, { useMemo, useState } from "react";
import {
  SCORE_COLORS,
  scoreToTier,
  formatMonthShort,
  formatDayOfMonth,
  formatAttendance,
  formatCategory,
  dateSortKey,
  ScoreBadge,
  AccommodationPill,
  SourceDot,
} from "./shared";
import ThinScroll from "../shared/ThinScroll";
import { Skeleton } from "../shared/Skeleton";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";

const EventCard = ({ event, onCardClick }) => {
  const score = Number(event.opportunity_score || 0);
  const tier = scoreToTier(score);
  const borderAccent = SCORE_COLORS[tier];
  // Spec (event_intelligence.md:490): "Card ... border thickness = score magnitude."
  // The left border already carries the tier color; its thickness is the second
  // channel. Scale the raw 0–100 score linearly to 2–8px so magnitude reads
  // continuously, not just in the 4 tier buckets. Border thickness (not card
  // height) keeps the fixed-width horizontal scroll row from going ragged.
  const accentWidth = Math.round(2 + (Math.min(100, Math.max(0, score)) / 100) * 6);
  // Spec (event_intelligence.md:545): card click opens the playbook if one exists,
  // otherwise the opportunity detail. Both paths route through onCardClick, so every
  // card is clickable — the parent decides which view to open based on has_playbook.
  const clickable = !!onCardClick;

  const handleClick = clickable ? () => onCardClick(event) : undefined;
  const handleKeyDown = clickable
    ? (domEvent) => {
        if (domEvent.key === "Enter" || domEvent.key === " ") {
          domEvent.preventDefault();
          onCardClick(event);
        }
      }
    : undefined;

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `${event.has_playbook ? "View playbook" : "View opportunity detail"} for ${event.event_name}` : undefined}
      className={`ei-event-card w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 transition-[box-shadow,border-color,transform] duration-150 flex flex-col gap-2 ${
        clickable ? "cursor-pointer focus:outline-none" : ""
      }`}
      style={{
        borderLeftWidth: `${accentWidth}px`,
        borderLeftColor: borderAccent,
        "--score-color": borderAccent,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <ScoreBadge score={score} />
        <div className="text-right leading-tight">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">
            {formatMonthShort(event.start_date)}
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {formatDayOfMonth(event.start_date)}
          </div>
        </div>
      </div>

      <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 line-clamp-2 min-h-[2.5rem]">
        {event.event_name}
      </div>

      <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
        {formatCategory(event.category)}
        {event.distance_miles != null && <> · {Number(event.distance_miles).toFixed(1)} mi</>}
        {event.attendance_estimate ? <> · {formatAttendance(event.attendance_estimate)} attendees</> : null}
      </div>

      {event.best_accommodation && <AccommodationPill type={event.best_accommodation} prefix="Best for" />}

      <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-100 dark:border-gray-800">
        {event.has_playbook
          ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="9" y1="13" x2="15" y2="13" />
                <line x1="9" y1="17" x2="13" y2="17" />
              </svg>
              Playbook ready
            </span>
          : <span className="text-[11px] text-gray-400 dark:text-gray-500">No playbook yet</span>}
        <SourceDot source={event.source} showLabel />
      </div>
    </div>
  );
};

const FilterLabel = ({ children }) => (
  <span className="text-xs uppercase tracking-wider font-bold text-gray-700 dark:text-gray-200">
    {children}
  </span>
);

const VALID_SOURCES = ["both", "ticketmaster", "serpapi"];
const VALID_SORTS = ["score", "date"];

const EventTimelinePanel = ({ loading, error, events, onCardClick, searchParams, updateParam }) => {
  // Filter state is URL-backed when the page supplies searchParams/updateParam
  // (spec event_intelligence.md:998-1001), falling back to local state otherwise
  // so the panel still works if used standalone.
  const urlBacked = !!(searchParams && updateParam);

  const [localScoreMin, setLocalScoreMin] = useState(30);
  const [localSource, setLocalSource] = useState("both");
  const [localExcluded, setLocalExcluded] = useState(new Set());
  const [localSort, setLocalSort] = useState("score");

  const scoreMin = urlBacked
    ? (() => { const parsed = parseInt(searchParams.get("score_min"), 10); return Number.isFinite(parsed) ? Math.min(100, Math.max(0, parsed)) : 30; })()
    : localScoreMin;
  const sourceFilter = urlBacked
    ? (VALID_SOURCES.includes(searchParams.get("source")) ? searchParams.get("source") : "both")
    : localSource;
  const sortBy = urlBacked
    ? (VALID_SORTS.includes(searchParams.get("sort")) ? searchParams.get("sort") : "score")
    : localSort;
  const excludedCategories = useMemo(() => {
    if (!urlBacked) return localExcluded;
    const raw = searchParams.get("category");
    return new Set(raw ? raw.split(",").map((segment) => segment.trim()).filter(Boolean) : []);
  }, [urlBacked, searchParams, localExcluded]);

  const setScoreMin = (value) => urlBacked ? updateParam("score_min", value === 30 ? "" : value) : setLocalScoreMin(value);
  const setSourceFilter = (value) => urlBacked ? updateParam("source", value === "both" ? "" : value) : setLocalSource(value);
  const setSortBy = (value) => urlBacked ? updateParam("sort", value === "score" ? "" : value) : setLocalSort(value);
  const setExcludedCategories = (updater) => {
    if (!urlBacked) { setLocalExcluded(updater); return; }
    const next = typeof updater === "function" ? updater(excludedCategories) : updater;
    const list = Array.from(next).join(",");
    updateParam("category", list);
  };

  const [visibleCount, setVisibleCount] = useState(1);

  const allCategories = useMemo(() => {
    const set = new Set();
    (events || []).forEach((eventItem) => { set.add(formatCategory(eventItem.category)); });
    return Array.from(set).sort();
  }, [events]);

  const filtered = useMemo(() => {
    const passed = (events || []).filter((eventItem) => {
      const score = Number(eventItem.opportunity_score || 0);
      if (score < scoreMin) return false;
      if (sourceFilter !== "both" && eventItem.source !== sourceFilter) return false;
      if (excludedCategories.has(formatCategory(eventItem.category))) return false;
      return true;
    });

    if (sortBy === "score") {
      return [...passed].sort((eventA, eventB) => {
        const scoreDiff = Number(eventB.opportunity_score || 0) - Number(eventA.opportunity_score || 0);
        if (scoreDiff !== 0) return scoreDiff;
        // Tiebreaker: chronological so same-score events still feel ordered.
        return dateSortKey(eventA.start_date) - dateSortKey(eventB.start_date);
      });
    }
    return [...passed].sort(
      (eventA, eventB) => dateSortKey(eventA.start_date) - dateSortKey(eventB.start_date)
    );
  }, [events, scoreMin, sourceFilter, excludedCategories, sortBy]);

  // Re-collapse the mobile list whenever the visible set changes. Keyed on `filtered`
  // rather than the individual filters: it already folds in all four of them PLUS
  // `events`, which the previous dep list omitted — so a date-range change that
  // swapped in a shorter event list left "Show more" expanded, and the count in the
  // button ("N remaining") could go negative against the new, smaller list.
  React.useEffect(() => { setVisibleCount(1); }, [filtered]);

  const toggleCategory = (cat) => {
    setExcludedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  return (
    <div className="p-5 border-t border-gray-200 dark:border-gray-800">
      <style>{`
        /* Hover/focus uses each card's score-tier color via the --score-color
           variable set inline. Lives here (not Tailwind) because the
           arbitrary-value hover:[--var] syntax is brittle and the var-driven
           border + ring needs both states to share the color. */
        .ei-event-card[role="button"]:hover {
          border-color: var(--score-color);
          box-shadow: 0 0 0 1px var(--score-color), 0 8px 16px -4px rgb(0 0 0 / 0.25);
          transform: translateY(-1px);
        }
        .ei-event-card[role="button"]:focus-visible {
          border-color: var(--score-color);
          box-shadow: 0 0 0 2px var(--score-color);
        }
      `}</style>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-center">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Event Timeline</h3>
            <InfoTip text="Each card shows the event's opportunity score, lead time, and category. Click a card to open its playbook, or its opportunity score breakdown if no playbook exists yet." />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Upcoming events scored as marketing opportunities.
          </p>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
          {loading ? "Loading…" : `${filtered.length} of ${(events || []).length} events`}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-4 pb-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3 w-full lg:w-auto lg:min-w-[260px]">
          {/* Spec (event_intelligence.md:549) specifies "Show events scoring ≥ [30]".
              Kept as "Min Score" deliberately — shorter, scannable, and the adjacent
              value box already shows the threshold. Logged as a deviation in
              ei_db_qs.md §2.4 (timeline slider). */}
          <FilterLabel>Min Score</FilterLabel>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={scoreMin}
            onChange={(event) => setScoreMin(Number(event.target.value))}
            className="flex-1 accent-[#32B1FF]"
          />
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={3}
            value={scoreMin}
            onChange={(event) => {
              const digitsOnly = event.target.value.replace(/\D/g, "");
              if (digitsOnly === "") { setScoreMin(0); return; }
              const parsed = Math.min(100, parseInt(digitsOnly, 10));
              setScoreMin(parsed);
            }}
            className="w-11 text-sm font-bold text-gray-800 dark:text-gray-100 text-center bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]/40 focus:border-[#32B1FF]"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FilterLabel>Source</FilterLabel>
          {[
            { key: "both", label: "Both" },
            { key: "ticketmaster", label: "Ticketmaster" },
            { key: "serpapi", label: "Google" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSourceFilter(opt.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                sourceFilter === opt.key
                  ? "bg-[#32B1FF] text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FilterLabel>Sort</FilterLabel>
          {[
            { key: "score", label: "Score" },
            { key: "date", label: "Date" }
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSortBy(opt.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                sortBy === opt.key
                  ? "bg-[#32B1FF] text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {allCategories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs uppercase tracking-wider font-bold text-gray-700 dark:text-gray-200 max-[499px]:w-full">Categories</span>
          {allCategories.map((cat) => {
            const active = !excludedCategories.has(cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() => toggleCategory(cat)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  active
                    ? "bg-[#32B1FF]/10 border-[#32B1FF]/40 text-[#32B1FF]"
                    : "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 line-through"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="shrink-0 w-[280px] h-[180px] rounded-xl" />
          ))}
        </div>
      ) : error ? (
        <ErrorState message={error} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          {(events || []).length === 0
            ? "No upcoming events discovered yet. The event scanner runs daily at 7:00 AM UTC."
            : "No events match the current filters."}
        </div>
      ) : (
        <div className="pt-1.5 pb-3">
          <ThinScroll className="flex gap-3 max-[499px]:flex-col min-[500px]:overflow-x-auto min-[500px]:-mx-1 min-[500px]:px-1 min-[500px]:pt-2 min-[500px]:pb-3">
            {filtered.map((eventItem, index) => (
              <div
                key={eventItem.event_id}
                className={`min-[500px]:w-[280px] min-[500px]:shrink-0 ${index >= visibleCount ? 'max-[499px]:hidden' : ''}`}
              >
                <EventCard event={eventItem} onCardClick={onCardClick} />
              </div>
            ))}
          </ThinScroll>
          {visibleCount < filtered.length && (
            <button
              type="button"
              onClick={() => setVisibleCount((prev) => Math.min(filtered.length, prev + 5))}
              className="mt-4 w-full text-sm font-medium text-[#32B1FF] hover:text-[#1f9fee] bg-[#32B1FF]/10 hover:bg-[#32B1FF]/15 border border-[#32B1FF]/20 rounded-lg px-3 py-2 transition max-[499px]:block min-[500px]:hidden"
            >
              Show more ({filtered.length - visibleCount} remaining)
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EventTimelinePanel;
